<template>
    <div>
      <div class="chart_holder" v-if="chartData && chartData.length">
        <plotly :data="chartData" :layout="chartLayout" :config="chartConfig" />
      </div>
    </div>
  </template>
  
  <script>
  import { Plotly } from 'vue-plotly'
  import defaultLayout from '@/components/helpers/plotly_style'
  import ReportDataHelper from '@/components/helpers/ReportDataHelper'
  import moment from 'moment'
  
  export default {
    name: 'BubbleChartPlotly',
    components: {
      Plotly
    },
    data() {
      return {
        // These are auto-populated from the parent (ReportPageParent).
        statsData: null,
        liveData: null,
        related_ids: null,
        reportParams: null,
        report: null
      }
    },
    props: {
      /*
        "stats" or "live"
        Tells the component whether to read from statsData or liveData.
      */
      data_source: {
        type: String,
        default: 'live'
      },
      /*
        Typically something like ['hours_tracked','odometer_reading'] if you want
        both fields retrieved. We only strictly need x_field, y_field, and size_field,
        but it's easiest to let "statistics" = [x_field, y_field, size_field] 
        so the backend fetches them in stats mode or device_props in live mode.
      */
      statistics: {
        type: [String, Array],
        default: () => []
      },
      /*
        For stats mode, how we combine multiple date-values for each device 
        (sum, max, min, count). For live, we simply read the single fields.
      */
      aggregration_mode: {
        type: String,
        default: 'sum'
      },
      /*
        For live data, which key in liveData to look at. Usually "devices".
      */
      data_key: {
        type: String,
        default: 'devices'
      },
      /*
        x_field, y_field, size_field are the property names or statistic keys 
        you want to plot on x-axis, y-axis, and bubble size, respectively.
      */
      x_field: {
        type: String,
        required: true
      },
      y_field: {
        type: String,
        required: true
      },
      size_field: {
        type: String,
        default: null // If null, we can treat all bubbles the same size, or skip.
      },
      /*
        If true, skip devices whose values are ~0 or missing.
      */
      hideZeroValues: {
        type: Boolean,
        default: false
      },
      /*
        If you want to limit the maximum number of devices plotted,
        you could set a maxPoints property and skip or group the rest 
        into "Others." (Example commented out below).
      */
      // maxPoints: {
      //   type: Number,
      //   default: 50
      // },
      /*
        If needed, fallback for missing or null data
      */
      null_values: {
        default: 0
      },
      /*
        Multiply final numeric values by this factor (e.g. convert meters→km).
        Applied to x_field and y_field. For size_field, you could do a separate prop if needed.
      */
      value_multiple: {
        type: Number,
        default: 1
      },
      /*
        Layout overrides for Plotly. e.g. { title: 'Correlation: Hours vs. Odometer' }
      */
      layout: {
        type: Object,
        default: () => ({})
      },
      /*
        Config overrides for Plotly. e.g. { responsive: true }
      */
      config: {
        type: Object,
        default: () => ({ responsive: true })
      },
      /*
        Minimum bubble size in px. 
      */
      minBubbleSize: {
        type: Number,
        default: 10
      },
      /*
        Maximum bubble size in px.
      */
      maxBubbleSize: {
        type: Number,
        default: 50
      }
    },
    methods: {
      async getRequiredStats() {
        if (this.data_source === 'stats') {
          let stats = []
          if (this.statistics) {
            let newStats
            if (this.statistics instanceof Promise) {
              newStats = await this.statistics
            } else {
              newStats = this.statistics
            }
            stats = stats.concat(newStats).flat()
          }
          return stats
        }
        return []
      },
      async getRequiredDeviceProps() {
        // For live data, we typically want the x_field, y_field, and size_field
        // plus any aggregator if needed. 
        // We'll unify them in "statistics" anyway, but here's the logic:
        if (this.data_source === 'live') {
          let fields = []
          if (this.statistics) {
            let newStats
            if (this.statistics instanceof Promise) {
              newStats = await this.statistics
            } else {
              newStats = this.statistics
            }
            fields = fields.concat(newStats).flat()
          }
          return fields
        }
        return []
      },
  
      // For stats: aggregator for each field across all dates => device totals
      _aggregateStatValue(statName, deviceId) {
        if (!this.statsData || !this.statsData.stats || !this.statsData.stats[statName]) {
          return 0
        }
        const deviceData = this.statsData.stats[statName][deviceId] || {}
        let aggregated = null
        this.statsData.labels.forEach(lbl => {
          const rawVal = deviceData[lbl] == null ? this.null_values : deviceData[lbl]
          const val = rawVal * this.value_multiple
          if (aggregated === null) {
            aggregated = val
          } else {
            switch (this.aggregration_mode) {
              case 'sum':
                aggregated += val
                break
              case 'max':
                aggregated = Math.max(aggregated, val)
                break
              case 'min':
                aggregated = Math.min(aggregated, val)
                break
              case 'count':
                aggregated += (val !== this.null_values ? 1 : 0)
                break
              default:
                aggregated += val
            }
          }
        })
        return aggregated == null ? 0 : aggregated
      },
  
      // For stats data: compute xVal, yVal, sizeVal for each device
      _gatherStatsBubbles() {
        let results = [] // array of { deviceId, xVal, yVal, sizeVal, labelName }
  
        if (!this.statsData || !this.statsData.labels) {
          return []
        }
  
        // We can collect the set of devices from the first field
        let statArr = Array.isArray(this.statistics) ? this.statistics : [this.statistics]
        if (!statArr.length) return []
  
        // We'll gather from the first stat for device IDs
        const mainStat = statArr[0]
        if (!this.statsData.stats[mainStat]) {
          return []
        }
  
        const deviceDict = this.statsData.stats[mainStat] // object: { deviceId: { 'YYYY/MM/DD': val, ... }, ... }
  
        for (const deviceId of Object.keys(deviceDict)) {
          const xVal = this._aggregateStatValue(this.x_field, deviceId) || 0
          const yVal = this._aggregateStatValue(this.y_field, deviceId) || 0
          let sVal = 10
          if (this.size_field) {
            sVal = this._aggregateStatValue(this.size_field, deviceId)
          }
  
          // device label from related_ids
          let labelName = deviceId
          if (this.related_ids && this.related_ids[deviceId]) {
            labelName = this.related_ids[deviceId]
          }
  
          results.push({ deviceId, xVal, yVal, sVal, labelName })
        }
  
        return results
      },
  
      // For live data: read xVal, yVal, sVal from the device object
      _gatherLiveBubbles() {
        let results = []
        if (!this.liveData || !this.liveData[this.data_key]) {
          return []
        }
        const devicesDict = this.liveData[this.data_key]
        for (const devKey of Object.keys(devicesDict)) {
          const devObj = devicesDict[devKey]
          let xVal = ReportDataHelper.getNestedField(devObj, this.x_field, this.null_values)
          let yVal = ReportDataHelper.getNestedField(devObj, this.y_field, this.null_values)
          xVal *= this.value_multiple
          yVal *= this.value_multiple
          let sVal = 10
          if (this.size_field) {
            sVal = ReportDataHelper.getNestedField(devObj, this.size_field, 10)
          }
          // device label
          let labelName = devKey
          if (devObj.device_name) {
            labelName = devObj.device_name
          }
  
          results.push({ deviceId: devKey, xVal, yVal, sVal, labelName })
        }
        return results
      },
  
      // Normalize size array into [minBubbleSize, maxBubbleSize]
      _rescaleSizes(sArr) {
        const sMin = Math.min(...sArr)
        const sMax = Math.max(...sArr)
        // if they are all the same or zero
        if (sMin === sMax) {
          return sArr.map(() => this.minBubbleSize)
        }
  
        return sArr.map(val => {
          return (
            this.minBubbleSize +
            (this.maxBubbleSize - this.minBubbleSize) * ((val - sMin) / (sMax - sMin))
          )
        })
      }
    },
    computed: {
      chartData() {
        let bubblePoints = []
        if (this.data_source === 'stats') {
          bubblePoints = this._gatherStatsBubbles()
        } else {
          bubblePoints = this._gatherLiveBubbles()
        }
  
        // If hideZeroValues, filter out points that have x=0 or y=0 or size=0
        if (this.hideZeroValues) {
          bubblePoints = bubblePoints.filter(
            p => Math.abs(p.xVal) > 1e-9 || Math.abs(p.yVal) > 1e-9 || Math.abs(p.sVal) > 1e-9
          )
        }
  
        // Optionally limit the max number of points if you want (uncomment if you add a maxPoints prop):
        // if (this.maxPoints && bubblePoints.length > this.maxPoints) {
        //   bubblePoints = bubblePoints.slice(0, this.maxPoints)
        // }
  
        if (!bubblePoints.length) {
          return []
        }
  
        let xArr = bubblePoints.map(p => p.xVal)
        let yArr = bubblePoints.map(p => p.yVal)
        let sizeArr = bubblePoints.map(p => p.sVal)
        let labels = bubblePoints.map(p => p.labelName)
  
        // Rescale marker sizes to a visually pleasing range
        let bubbleSizes = this._rescaleSizes(sizeArr)
  
        // Single trace with mode='markers' => bubble chart
        let trace = {
          type: 'scatter',
          mode: 'markers',
          x: xArr,
          y: yArr,
          text: labels, // This can be used in hover
          hovertemplate: '%{text}<br>X=%{x}<br>Y=%{y}<br>Size=%{marker.size}',
          marker: {
            size: bubbleSizes,
            // You can set a color array if you want to color each device differently.
            // Or you can pick a single color scale. E.g.:
            // colorscale: 'Viridis', color: sizeArr
            // shows bubble color based on size
            opacity: 0.8
          },
          // If you want a legend label:
          name: 'Devices'
        }
  
        return [trace]
      },
  
      chartLayout() {
        let newLayout = {
          ...defaultLayout,
          ...this.layout
        }
  
        // Provide default axis titles if user hasn't set them
        if (!newLayout.xaxis) {
          newLayout.xaxis = { title: this.x_field }
        } else if (!newLayout.xaxis.title) {
          newLayout.xaxis.title = this.x_field
        }
  
        if (!newLayout.yaxis) {
          newLayout.yaxis = { title: this.y_field }
        } else if (!newLayout.yaxis.title) {
          newLayout.yaxis.title = this.y_field
        }
  
        // Bubble charts often benefit from some margin
        if (!newLayout.margin) {
          newLayout.margin = {}
        }
        if (!newLayout.margin.l) newLayout.margin.l = 60
        if (!newLayout.margin.r) newLayout.margin.r = 50
        if (!newLayout.margin.t) newLayout.margin.t = 60
        if (!newLayout.margin.b) newLayout.margin.b = 50
  
        return newLayout
      },
  
      chartConfig() {
        return this.config
      }
    }
  }
  </script>
  
  <style scoped>
  .chart_holder {
    width: 100%;
    height: 100%;
    min-height: 400px;
  }
  </style>
  